import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getProduct, createCheckout, trackView } from "@/lib/api";
import { Lightning, ShieldCheck, CreditCard, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Checkout() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    shipping_address: "",
    quantity: 1,
    notes: "",
  });

  useEffect(() => {
    getProduct(productId)
      .then(p => setProduct(p))
      .catch(() => setNotFound(true));
    // Fire analytics view (public, no auth)
    const utm = new URLSearchParams(window.location.search).get("utm_source") || "";
    trackView(productId, document.referrer || "", utm).catch(() => {});
  }, [productId]);

  const handleBuy = async () => {
    const isDigital = product?.product_type !== "physical";
    if (!form.customer_name || !form.customer_email) {
      toast.error("Please fill in name and email");
      return;
    }
    if (!isDigital && !form.shipping_address) {
      toast.error("Shipping address is required for physical products");
      return;
    }
    setLoading(true);
    try {
      const res = await createCheckout({
        product_id: productId,
        quantity: parseInt(form.quantity) || 1,
        origin_url: window.location.origin,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        shipping_address: form.shipping_address,
        notes: form.notes,
      });
      window.location.href = res.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-arena-bg text-arena-text font-mono flex items-center justify-center p-8">
        <div className="text-center">
          <div className="font-heading text-3xl font-bold mb-2">PRODUCT NOT FOUND</div>
          <div className="text-arena-muted text-sm">This payment link is invalid or the product has been removed.</div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-arena-bg text-arena-text font-mono flex items-center justify-center">
        <CircleNotch size={32} weight="bold" className="animate-spin text-arena-primary" />
      </div>
    );
  }

  const subtotal = (product.price * (parseInt(form.quantity) || 1)).toFixed(2);
  const isDigitalProduct = product.product_type !== "physical";

  return (
    <div className="min-h-screen bg-arena-bg text-arena-text font-mono" data-testid="checkout-page">
      {/* Header */}
      <header className="border-b border-arena-border p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lightning size={20} weight="fill" className="text-arena-primary" />
            <span className="font-heading font-bold tracking-tight">PEP<span className="text-arena-primary">.</span>STYLES</span>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-arena-muted">
            <ShieldCheck size={14} weight="bold" className="text-arena-success" />
            Secure Stripe Checkout
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Product panel */}
        <div className="border border-arena-border bg-arena-surface">
          {product.image_url && (
            <div className="aspect-video border-b border-arena-border overflow-hidden bg-black">
              <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="p-6">
            <div className="flex items-center gap-2 mb-2">
              <div className="overline">// product</div>
              <span className={`text-[9px] tracking-widest uppercase px-1.5 py-0.5 border ${isDigitalProduct ? 'border-arena-primary text-arena-primary' : 'border-blue-400 text-blue-400'}`}>
                {isDigitalProduct ? 'DIGITAL' : 'PHYSICAL'}
              </span>
            </div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">{product.name}</h1>
            {product.sku && <div className="text-[10px] text-arena-muted mt-1">SKU: {product.sku}</div>}
            <div className="font-heading text-4xl font-bold text-arena-primary mt-4">
              ${product.price.toFixed(2)}
            </div>
            {product.description && (
              <p className="text-sm text-arena-muted mt-4 whitespace-pre-wrap leading-relaxed">{product.description}</p>
            )}

            <div className="mt-6 border-t border-arena-border pt-4 text-xs space-y-2">
              {isDigitalProduct ? (
                <>
                  <div className="flex justify-between"><span className="text-arena-muted">Delivery</span><span className="text-arena-success">Instant — right after payment</span></div>
                  <div className="flex justify-between"><span className="text-arena-muted">Format</span><span>Digital download</span></div>
                  <div className="flex justify-between"><span className="text-arena-muted">Access</span><span>30-day secure link</span></div>
                </>
              ) : (
                <>
                  <div className="flex justify-between"><span className="text-arena-muted">Ships from</span><span>US warehouse</span></div>
                  <div className="flex justify-between"><span className="text-arena-muted">Delivery</span><span>7–14 business days</span></div>
                  <div className="flex justify-between"><span className="text-arena-muted">Returns</span><span>30-day money-back</span></div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Checkout form */}
        <div className="border border-arena-border bg-arena-surface p-6">
          <div className="overline mb-2">// checkout</div>
          <h2 className="font-heading text-2xl font-bold tracking-tight mb-4">SHIPPING &amp; DETAILS</h2>

          <div className="space-y-4">
            <div>
              <label className="overline block mb-1">Full Name *</label>
              <input
                data-testid="checkout-name"
                value={form.customer_name}
                onChange={e => setForm({ ...form, customer_name: e.target.value })}
                className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
              />
            </div>
            <div>
              <label className="overline block mb-1">Email *</label>
              <input
                data-testid="checkout-email"
                type="email"
                value={form.customer_email}
                onChange={e => setForm({ ...form, customer_email: e.target.value })}
                className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
              />
              <div className="text-[10px] text-arena-muted mt-1">{isDigitalProduct ? "Your download link is sent here." : "Order confirmation & tracking info sent here."}</div>
            </div>
            {!isDigitalProduct && (
              <div>
                <label className="overline block mb-1">Shipping Address *</label>
                <textarea
                  data-testid="checkout-address"
                  value={form.shipping_address}
                  onChange={e => setForm({ ...form, shipping_address: e.target.value })}
                  rows={3}
                  placeholder="Street, City, State, ZIP, Country"
                  className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none resize-none"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-1">Quantity</label>
                <input
                  data-testid="checkout-quantity"
                  type="number"
                  min="1"
                  value={form.quantity}
                  onChange={e => setForm({ ...form, quantity: e.target.value })}
                  className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
                />
              </div>
              <div>
                <label className="overline block mb-1">Subtotal</label>
                <div className="w-full border border-arena-border px-3 py-2 text-sm font-heading font-bold text-arena-primary">${subtotal}</div>
              </div>
            </div>
            <div>
              <label className="overline block mb-1">Order Notes (optional)</label>
              <input
                data-testid="checkout-notes"
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="Gift wrap, color preference, etc."
                className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
              />
            </div>
          </div>

          <button
            onClick={handleBuy}
            disabled={loading}
            data-testid="pay-now-btn"
            className="w-full mt-6 bg-arena-primary text-black px-6 py-4 text-sm tracking-widest uppercase font-bold hover:bg-arena-primaryHover flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <CreditCard size={16} weight="bold" />}
            {loading ? "Redirecting..." : `Pay $${subtotal} securely`}
          </button>

          <div className="mt-4 text-[10px] text-arena-muted text-center">
            You&apos;ll be redirected to Stripe. All card details are handled by Stripe — never touched by our servers.
          </div>
        </div>
      </div>
    </div>
  );
}
